import { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
// @mui
import { Box, Button, Dialog, DialogContent, DialogTitle, Fab, Skeleton, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
// hooks
import useResponsive from '../../../hooks/useResponsive';
// components
import Iconify from '../../../components/iconify';
import { MIconButton } from '../../../components/@mui-extend';
// redux
import { useDispatch, useSelector } from '../../../redux/store';
import { getServices } from '../../../redux/slices/course';
//
import ServiceList from './ServiceList';

// ----------------------------------------------------------------------

const StyledFab = styled(Fab)(({ theme }) => ({
  left: theme.spacing(2),
  bottom: theme.spacing(2),
  position: 'fixed',
  zIndex: theme.zIndex.speedDial,
}));

// ----------------------------------------------------------------------
// Homepage service list.
// - md and up: rendered inline (right after the hero) as a 2-column grid.
// - below md: a floating button opens the same list inside a popup, so the
//   homepage stays compact on small screens.

export default function ServiceListWidget() {
  const isDesktop = useResponsive('up', 'md');
  const dispatch = useDispatch();
  const { services, isLoading } = useSelector((state) => state.course);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    dispatch(getServices());
  }, [dispatch]);

  const items = Array.isArray(services) ? services.filter((service) => service && service.slug) : [];

  const handleOpen = () => setOpen(true);

  const handleClose = () => setOpen(false);

  if (!items.length) {
    if (!isLoading || !isDesktop) {
      return null;
    }

    // Compact placeholder so the hero is not followed by a large empty gap.
    return (
      <Box sx={{ mx: { md: 10 }, my: 8 }}>
        {[0, 1, 2].map((skeleton) => (
          <Skeleton key={skeleton} variant="rounded" height={56} sx={{ mb: 1, borderRadius: 1.5 }} />
        ))}
      </Box>
    );
  }

  if (!isDesktop) {
    return (
      <>
        <StyledFab variant="extended" color="primary" onClick={handleOpen} aria-label="Open our services">
          <Iconify icon="ic:round-miscellaneous-services" width={22} sx={{ mr: 1 }} />
          Our Services
        </StyledFab>

        <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', py: 2 }}>
            <Iconify icon="ic:round-miscellaneous-services" width={22} sx={{ mr: 1, color: '#DF3E30' }} />
            Our Services
            <Box sx={{ flexGrow: 1 }} />
            <MIconButton onClick={handleClose} aria-label="Close our services">
              <Iconify icon="eva:close-fill" width={20} />
            </MIconButton>
          </DialogTitle>

          <DialogContent sx={{ pt: 0 }}>
            <ServiceList services={items} onItemClick={handleClose} />

            <Button
              to="/services"
              component={RouterLink}
              onClick={handleClose}
              fullWidth
              variant="contained"
              sx={{ mt: 2 }}
            >
              VIEW ALL SERVICES
            </Button>
          </DialogContent>
        </Dialog>
      </>
    );
  }

  return (
    <Box sx={{ my: 8 }}>
      <Typography variant="subtitle1" sx={{ color: '#DF3E30', display: 'block', textAlign: 'center' }}>
        What we do
      </Typography>
      <Typography component="h2" variant="h3" sx={{ color: '#000', display: 'block', textAlign: 'center', mb: 3 }}>
        Our Services
      </Typography>

      <Box sx={{ mx: { md: 10 } }}>
        <ServiceList services={items} grid />

        <Box sx={{ textAlign: 'center', mt: 2 }}>
          <Button to="/services" component={RouterLink} size="large" variant="outlined">
            VIEW ALL SERVICES
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
